import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import API from "../../services/api";
import { roles, alphabets, BrandsTypes, capitalizeFirstLetter } from "../../utils/constants";
import { SetComponentId, setSubordinates } from "../../redux/counterSlice";
import { Toast } from "../../shared/Toast";
import closeRed from "../../assets/images/close-red.svg";
import closeSvg from "../../assets/images/uncheck.svg";
import "./Collections.scss";

/* ------------------------------------------------------------------ *
 *  Collections (Sales)
 *  Money billed, received and outstanding — by client. Expand a client
 *  to see it order by order, then drill into its invoices / transactions.
 *
 *  Main users get the same "Select Users" picker the other sales tabs
 *  use, so they can view other salespersons' collections. Month is a
 *  month-range picker (strictly month, no day granularity).
 *
 *  Data comes from POST /collections/getCollections. Response shape:
 *    [{ id, name, category, orders: [
 *         { id, name, month:'YYYY-MM', spent,
 *           invs:[{ no, amount, status, date }],
 *           txns:[{ mode, amount, status, date }] } ] }]
 * ------------------------------------------------------------------ */

// colour a category chip: known categories keep their brand colour, anything
// else gets a stable colour from the palette (categories are dynamic in prod).
const CATEGORY_COLORS = {
  fmcg: { bg: "#efe9fb", fg: "#5b3aa8" },
  retail: { bg: "#e6f0fe", fg: "#1a5fc0" },
  healthcare: { bg: "#e3f5ea", fg: "#137333" },
  education: { bg: "#fdf0dd", fg: "#a05a00" },
  lifestyle: { bg: "#fce7f0", fg: "#b0195e" },
  other: { bg: "#f1f3f4", fg: "#5f6368" },
};
const CHIP_PALETTE = [
  { bg: "#efe9fb", fg: "#5b3aa8" },
  { bg: "#e6f0fe", fg: "#1a5fc0" },
  { bg: "#e3f5ea", fg: "#137333" },
  { bg: "#fdf0dd", fg: "#a05a00" },
  { bg: "#fce7f0", fg: "#b0195e" },
  { bg: "#e5f6f8", fg: "#0b6b78" },
  { bg: "#f1f3f4", fg: "#5f6368" },
];
const catStyle = (label) => {
  const name = (label || "Other").trim() || "Other";
  const key = name.toLowerCase();
  if (CATEGORY_COLORS[key]) return { label: name, ...CATEGORY_COLORS[key] };
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return { label: name, ...CHIP_PALETTE[h % CHIP_PALETTE.length] };
};

// lucide icon per transaction mode (labels mirror orderPaymentTypes)
const TXN_ICONS = {
  Cashfree: "credit-card",
  "Cashfree Order": "credit-card",
  Cheque: "scroll-text",
  Cash: "banknote",
  "IMPS/NEFT/RTGS": "landmark",
  Invoice: "file-text",
};

/* ---------------------------- helpers ---------------------------- */
const MNAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const monthLabelDate = (date) => `${MNAMES[date.getMonth()]} ${date.getFullYear()}`;
const monthLabelKey = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-").map(Number);
  return `${MNAMES[m - 1]} ${y}`;
};

const rangeLabel = (a, b) => {
  if (!a) return "—";
  const start = monthLabelDate(a);
  if (!b || monthKey(a) === monthKey(b)) return start;
  return `${start} – ${monthLabelDate(b)}`;
};

const inr = (n) => {
  if (n === null || n === undefined) return "—";
  const s = Math.round(n).toString();
  let last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  if (rest) last3 = "," + last3;
  rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return "₹" + rest + last3;
};

// The TDS line under a Received figure: how much the client withheld, and how much of
// it has actually come back from the government (finance marks it received, and that
// TDS transaction gets approved).
const tdsNote = (tds, tdsReceived) => {
  const total = Number(tds || 0);
  if (total <= 0) return null;
  const got = Number(tdsReceived || 0);
  if (got >= total) return `+ ${inr(total)} TDS · all received`;
  if (got > 0) return `+ ${inr(total)} TDS · ${inr(got)} received`;
  return `+ ${inr(total)} TDS · none received`;
};

const statusStyle = (s) => {
  if (s === "Paid" || s === "Success") return { bg: "#e6f4ea", fg: "#188038" };
  if (s === "Raised" || s === "Pending") return { bg: "#fef7e0", fg: "#b06000" };
  if (s === "Overdue" || s === "Failed") return { bg: "#fce8e6", fg: "#c5221f" };
  return { bg: "#f1f3f4", fg: "#5f6368" };
};

const orderStatusStyle = (received, pending, tdsOnlyPending) => {
  if (pending <= 0) {
    if (tdsOnlyPending) return { label: "Pending (TDS)", bg: "#fef7e0", fg: "#b06000" };
    return { label: "Paid", bg: "#e6f4ea", fg: "#188038" };
  }
  if (received > 0) return { label: "Partial", bg: "#e8f0fe", fg: "#1a73e8" };
  return { label: "Unpaid", bg: "#fce8e6", fg: "#c5221f" };
};

const initials = (name) =>
  (name.replace(/[^a-zA-Z ]/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("") || "C").toUpperCase();

function Collections() {
  const dispatch = useDispatch();
  dispatch(SetComponentId(100));
  const navigate = useNavigate();

  const loginRole = localStorage.getItem("role");
  // list of salespersons a main user can view (same source the other sales tabs use)
  const salesUsersList = useSelector((state) => state.Head.subordinates) || [];
  const allowedNewUserTypes = [...new Set(salesUsersList.map((item) => item.new_user_type))];

  const firstOfThisMonth = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [monthRange, setMonthRange] = useState([firstOfThisMonth, firstOfThisMonth]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  /* ---- "Select Users" picker state (identical to the other sales tabs) ---- */
  const [selectedList, setSelectedList] = useState(
    localStorage.getItem("selectedSalesList") && JSON.parse(localStorage.getItem("selectedSalesList")).length
      ? salesUsersList.filter((item) => JSON.parse(localStorage.getItem("selectedSalesList"))?.includes(item.value))
      : []
  );
  const [salesPerson_id, setSalesPerson_id] = useState(
    salesUsersList?.length > 1
      ? localStorage.getItem("selectedSalesList") && JSON.parse(localStorage.getItem("selectedSalesList")).length
        ? salesUsersList.filter((item) => JSON.parse(localStorage.getItem("selectedSalesList"))?.includes(item.value))[0]?.value || -1
        : salesUsersList[0]?.value
      : -1
  );
  const [tempSalesUsersList, setTempSalesUsersList] = useState(salesUsersList);
  const [filterActiveLetters, setFilterActiveLetters] = useState([
    ...new Set(salesUsersList.map((item) => item?.label[0]?.toUpperCase()).sort()),
  ]);
  const [searchFilter, setSearchFilter] = useState("");
  const [showUsersFilter, setShowUsersFilter] = useState(false);
  const [newUserTypeInfilter, setNewUserTypeInfilter] = useState(0);

  const [clients, setClients] = useState([]);
  const [loader, setLoader] = useState(false);
  const [expanded, setExpanded] = useState({}); // clientId -> bool
  const [panel, setPanel] = useState({}); // orderId -> 'invoices' | 'transactions'
  const [refreshKey, setRefreshKey] = useState(0);

  // "Record payment" modal (salesperson adds a cheque/cash/IMPS payment to an order)
  const PAY_MODES = [
    { label: "Cheque", value: 2 },
    { label: "Cash", value: 3 },
    { label: "IMPS/NEFT/RTGS", value: 4 },
  ];
  const EMPTY_PAY_FORM = { mode: "regular", payment_type: 2, amount: "", transaction_id: "", tds: "", invoice_no: "", payment_date: "", pan_no: "", mopd_id: "" };
  const [payModal, setPayModal] = useState({ open: false, order: null });
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [pools, setPools] = useState([]); // approved multi-order payment pools

  // multi-order payment pool creation
  const EMPTY_POOL_FORM = { payment_type: 2, amount: "", transaction_id: "", pan_no: "" };
  const [poolModal, setPoolModal] = useState(false);
  const [poolForm, setPoolForm] = useState(EMPTY_POOL_FORM);
  const [poolSubmitting, setPoolSubmitting] = useState(false);

  // sales persons only
  useEffect(() => {
    if (loginRole !== roles.salesperson) navigate("/pagenotfound");
  }, []);

  // populate the subordinates list if not already cached (header only loads it on the dashboard)
  useEffect(() => {
    if (salesUsersList.length) return;
    const fetchSubordinates = async () => {
      try {
        const res = await API({
          url: "/user/sales_persons/getsubordinates",
          method: "POST",
          responseType: "json",
        });
        if (res?.code === 200 && Array.isArray(res.data)) dispatch(setSubordinates(res.data));
      } catch (error) {
        console.error("Error @ Collections fetchSubordinates", error);
      }
    };
    fetchSubordinates();
  }, []);

  // when the subordinates list arrives, hydrate the picker state
  useEffect(() => {
    if (!salesUsersList.length) return;
    setTempSalesUsersList(salesUsersList);
    setFilterActiveLetters([...new Set(salesUsersList.map((item) => item?.label[0]?.toUpperCase()).sort())]);
    if (salesUsersList.length > 1) {
      const saved = JSON.parse(localStorage.getItem("selectedSalesList") || "[]");
      const sel = saved.length ? salesUsersList.filter((item) => saved.includes(item.value)) : [];
      setSelectedList(sel);
      setSalesPerson_id(sel[0]?.value || salesUsersList[0]?.value);
    }
  }, [salesUsersList]);

  // fetch collections whenever the month range or the viewed salesperson changes
  useEffect(() => {
    if (!(monthRange[0] && monthRange[1])) return;
    const fetchCollections = async () => {
      setLoader(true);
      try {
        const res = await API({
          url: "/collections/getCollections",
          method: "POST",
          responseType: "json",
          data: {
            start_month: monthKey(monthRange[0]),
            end_month: monthKey(monthRange[1]),
            sales_person_id: salesPerson_id === -1 ? undefined : salesPerson_id,
            sales_id: selectedList.length ? selectedList.map((item) => item.value) : undefined,
          },
        });
        setClients(res?.code === 200 && Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Error @ Collections fetchCollections", error);
        setClients([]);
      } finally {
        setLoader(false);
      }
    };
    fetchCollections();
  }, [monthRange, salesPerson_id, refreshKey]);

  /* ---------------- picker handlers (same as other tabs) ---------------- */
  const salesTabChange = (sales_id) => {
    setSalesPerson_id(sales_id);
  };

  const scrollToSection = (id) => {
    document?.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleRemove = (value) => {
    const tempArray = selectedList.filter((item) => item.value !== +value);
    setSelectedList(tempArray);
    if (+value === salesPerson_id) setSalesPerson_id(+tempArray[0]?.value || -1);
    localStorage.setItem("selectedSalesList", JSON.stringify(tempArray.map((item) => item.value)));
  };

  const handleNewUsrTypChange = (new_user_type) => {
    try {
      setNewUserTypeInfilter(new_user_type);
      let filteredArray = salesUsersList;
      if (searchFilter) filteredArray = filteredArray.filter((item) => item.label.toLowerCase().includes(searchFilter));
      if (+new_user_type) filteredArray = filteredArray.filter((item) => +item.new_user_type === +new_user_type);
      setTempSalesUsersList(filteredArray);
      setFilterActiveLetters([...new Set(filteredArray.map((item) => item?.label[0]?.toUpperCase()).sort())]);
    } catch (error) {
      console.error("Error @ handleNewUsrTypChange", error);
    }
  };

  const closeUsersFilter = () => {
    try {
      if (selectedList.length === 0) {
        Toast("error", "Select Atleast a User");
      } else {
        setSalesPerson_id(selectedList[0].value);
        setShowUsersFilter(false);
      }
    } catch (error) {
      console.error("Error @ closeUsersFilter", error);
    }
  };

  const toggleClient = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const togglePanel = (orderId, which) =>
    setPanel((prev) => ({ ...prev, [orderId]: prev[orderId] === which ? null : which }));

  const fetchPools = async () => {
    try {
      const res = await API({
        url: "/collections/getMopPools",
        method: "POST",
        responseType: "json",
        data: { sales_person_id: salesPerson_id === -1 ? undefined : salesPerson_id },
      });
      setPools(res?.code === 200 && Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error @ Collections fetchPools", error);
      setPools([]);
    }
  };

  const openPayModal = (order) => {
    setPayForm(EMPTY_PAY_FORM);
    setPayModal({ open: true, order });
    fetchPools();
  };
  const closePayModal = () => setPayModal({ open: false, order: null });
  const setPay = (field, value) => setPayForm((prev) => ({ ...prev, [field]: value }));

  const submitPayment = async () => {
    const order = payModal.order;
    if (!order) return;
    if (!(Number(payForm.amount) > 0)) return Toast("error", "Enter a valid amount");

    const isPool = payForm.mode === "pool";
    if (isPool && !payForm.mopd_id) return Toast("error", "Select a multi-order payment");
    if (!isPool && !payForm.transaction_id.trim()) return Toast("error", "Transaction ID is required");

    setPaySubmitting(true);
    try {
      const res = await API({
        url: isPool ? "/collections/allocateMop" : "/collections/addPayment",
        method: "POST",
        responseType: "json",
        data: isPool
          ? {
              mopd_id: payForm.mopd_id,
              order_id: order.id,
              amount: payForm.amount,
              tds: payForm.tds || undefined,
              invoice_no: payForm.invoice_no || undefined,
              payment_date: payForm.payment_date || undefined,
            }
          : {
              order_id: order.id,
              payment_type: payForm.payment_type,
              amount: payForm.amount,
              transaction_id: payForm.transaction_id.trim(),
              tds: payForm.tds || undefined,
              invoice_no: payForm.invoice_no || undefined,
              payment_date: payForm.payment_date || undefined,
              pan_no: payForm.pan_no || undefined,
            },
      });
      if (res?.code === 200) {
        Toast("success", isPool ? "Allocated from multi-order payment" : "Payment submitted for approval");
        closePayModal();
        setRefreshKey((k) => k + 1);
      } else {
        Toast("error", res?.message || "Failed to add payment");
      }
    } catch (error) {
      console.error("Error @ Collections submitPayment", error);
      Toast("error", "Something went wrong");
    } finally {
      setPaySubmitting(false);
    }
  };

  const openPoolModal = () => {
    setPoolForm(EMPTY_POOL_FORM);
    setPoolModal(true);
  };
  const closePoolModal = () => setPoolModal(false);
  const setPoolField = (field, value) => setPoolForm((prev) => ({ ...prev, [field]: value }));

  const submitPool = async () => {
    if (!(Number(poolForm.amount) > 0)) return Toast("error", "Enter a valid total amount");
    if (!poolForm.transaction_id.trim()) return Toast("error", "Transaction ID is required");
    setPoolSubmitting(true);
    try {
      const res = await API({
        url: "/collections/createMopPool",
        method: "POST",
        responseType: "json",
        data: {
          payment_type: poolForm.payment_type,
          amount: poolForm.amount,
          transaction_id: poolForm.transaction_id.trim(),
          pan_no: poolForm.pan_no || undefined,
          sales_person_id: salesPerson_id === -1 ? undefined : salesPerson_id,
        },
      });
      if (res?.code === 200) {
        Toast("success", "Multi-order payment created — pending finance approval");
        closePoolModal();
      } else {
        Toast("error", res?.message || "Failed to create multi-order payment");
      }
    } catch (error) {
      console.error("Error @ Collections submitPool", error);
      Toast("error", "Something went wrong");
    } finally {
      setPoolSubmitting(false);
    }
  };

  // category dropdown is built from whatever categories the data actually has
  const categoryOptions = useMemo(() => {
    const set = new Set(clients.map((c) => (c.category || "Other")).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [clients]);

  // ---- derive the view model (filter by month range / category / search) ----
  const startKey = monthKey(monthRange[0]);
  const endKey = monthKey(monthRange[1] || monthRange[0]);
  const q = search.trim().toLowerCase();

  const viewClients = clients
    .filter((b) => category === "all" || (b.category || "Other") === category)
    .filter((b) => !q || (b.name || "").toLowerCase().includes(q))
    .map((b) => {
      const orders = (b.orders || []).filter((o) => o.month >= startKey && o.month <= endKey);
      return { b, orders };
    })
    .filter((x) => x.orders.length > 0)
    .map(({ b, orders }) => {
      const cat = catStyle(b.category);
      let orderValue = 0;
      let billed = 0;
      let gst = 0;
      let burned = 0;
      let received = 0;
      let tds = 0;
      let tdsReceived = 0;

      const orderVMs = orders.map((o) => {
        const oSpent = Number(o.spent || 0);
        const oReceived = Number(o.received || 0);
        const oTds = Number(o.tds || 0);
        const oBurned = Number(o.burned || 0);
        // the part of that TDS that has actually come back from the govt and been
        // approved as a TDS transaction; the rest is still awaited
        const oTdsReceived = Number(o.tdsReceived || 0);
        // TDS is deducted by the client and paid to the govt on our behalf, so it
        // settles the receivable along with the money received.
        const oCollected = oReceived + oTds;
        const oPending = Math.max(oSpent - oCollected, 0);
        orderValue += Number(o.orderValue || 0);
        billed += oSpent;
        burned += oBurned;
        gst += Number(o.gst || 0);
        received += oReceived;
        tds += oTds;
        const oPendingExclTds = Math.max(oSpent - oReceived, 0);
        const st = orderStatusStyle(oCollected, oPending, oTds > 0 && oPendingExclTds > 0);
        tdsReceived += oTdsReceived;
        return {
          ...o,
          spent: oSpent,
          received: oReceived,
          tds: oTds,
          burned: oBurned,
          tdsReceived: oTdsReceived,
          pending: oPending,
          status: st,
          invCount: (o.invs || []).length,
          txnCount: (o.txns || []).length,
        };
      });

      const collected = received + tds;
      const pending = Math.max(billed - collected, 0);
      const collPct = billed > 0 ? Math.round((collected / billed) * 100) : 0;
      const collColor = collPct >= 100 ? "#188038" : collPct >= 50 ? "#1a73e8" : "#f9a825";

      return {
        id: b.id,
        name: b.name,
        initials: initials(b.name || "Client"),
        cat,
        orderCount: orderVMs.length,
        orderValue,
        billed,
        gst,
        burned,
        received,
        tds,
        tdsReceived,
        pending,
        collPct,
        collColor,
        orders: orderVMs,
      };
    });

  const isMultiUser = salesUsersList?.length > 1;

  // totals across the currently-visible clients
  const totals = viewClients.reduce(
    (a, b) => {
      a.orderValue += b.orderValue;
      a.billed += b.billed;
      a.gst += b.gst;
      a.burned += b.burned;
      a.received += b.received;
      a.tds += b.tds;
      a.tdsReceived += b.tdsReceived;
      a.pending += b.pending;
      return a;
    },
    { orderValue: 0, billed: 0, gst: 0, burned: 0, received: 0, tds: 0, tdsReceived: 0, pending: 0 }
  );
  const totalCollPct = totals.billed > 0 ? Math.round(((totals.received + totals.tds) / totals.billed) * 100) : 0;

  return (
    <div className="collections-page">
      {/* header */}
      <div className="cl-head">
        <div>
          <h1 className="cl-title">Collections</h1>
          <div className="cl-subtitle">
            Money billed, received and outstanding — by client. Expand a client to see it order by order.
          </div>
        </div>
        <div className="cl-count-pill">
          <SlIcon library="lucide" name="users" />
          <b>{viewClients.length}</b>&nbsp;clients&nbsp;·&nbsp;{rangeLabel(monthRange[0], monthRange[1])}
        </div>
      </div>

      {/* filters */}
      <div className="cl-filters">
        {/* month RANGE picker — strictly month, project react-datepicker */}
        <div className="cl-month">
          <SlIcon library="lucide" name="calendar-days" />
          <div className="cl-month-field">
            <span className="cl-month-label">Month range</span>
            <DatePicker
              selected={monthRange[0]}
              onChange={(update) => setMonthRange(update)}
              startDate={monthRange[0]}
              endDate={monthRange[1]}
              dateFormat="MMM yyyy"
              showMonthYearPicker
              selectsRange
              maxDate={new Date()}
              className="cl-month-input"
            />
          </div>
        </div>

        {/* category */}
        <div className="cl-field cl-field-category">
          <label>Category</label>
          <SlIcon library="lucide" name="tag" className="cl-lead-ic" />
          <select
            className="cl-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categoryOptions.map((o) => (
              <option key={o} value={o}>
                {o === "all" ? "All categories" : o}
              </option>
            ))}
          </select>
          <SlIcon library="lucide" name="chevron-down" className="cl-trail-ic" />
        </div>

        {/* search */}
        <div className="cl-field cl-field-search">
          <label>Search</label>
          <SlIcon library="lucide" name="search" className="cl-lead-ic" />
          <input
            className="cl-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Client or brand name…"
          />
        </div>
      </div>

      {/* salesperson selector — the shared "Select Users" design used by the
          other sales tabs. Main users pick whose collections to view. */}
      <div className="cl-users-row">
        {isMultiUser ? (
          <>
            {selectedList.map((item) => (
              <button
                key={item.value}
                className={`cl-user-chip ${salesPerson_id === item.value ? "is-active" : ""}`}
                onClick={() => salesTabChange(item.value)}
              >
                {capitalizeFirstLetter(item.label)}
                <span
                  className="cl-user-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(item.value);
                  }}
                >
                  <img src={closeRed} alt="remove" />
                </span>
              </button>
            ))}
            <div className="select-sales-users" onClick={() => setShowUsersFilter(true)}>
              Select Users
            </div>
          </>
        ) : (
          <button className="cl-user-chip is-single is-active">
            {capitalizeFirstLetter(localStorage.getItem("name") || "My collections")}
          </button>
        )}
        <button className="cl-mop-btn" onClick={openPoolModal}>
          <SlIcon library="lucide" name="layers" />
          New multi-order payment
        </button>
      </div>

      {/* summary box */}
      {!loader && viewClients.length > 0 && (
        <div className="cl-summary">
          <div className="cl-summary-item">
            <span className="cl-summary-label">Order Value</span>
            <span className="cl-summary-value">{inr(totals.orderValue)}</span>
          </div>
          <div className="cl-summary-item">
            <span className="cl-summary-label">Billed</span>
            <span className="cl-summary-value">{inr(totals.billed)}</span>
            {totals.gst > 0 && <span className="cl-summary-sub cl-gst">incl. {inr(totals.gst)} GST (18%)</span>}
            {totals.burned > 0 && <span className="cl-summary-sub">{inr(totals.burned)} excl. GST</span>}
          </div>
          <div className="cl-summary-item is-received">
            <span className="cl-summary-label">Received</span>
            <span className="cl-summary-value">
              {inr(totals.received)}
              {totals.tds > 0 && <small className="cl-summary-sub"> {tdsNote(totals.tds, totals.tdsReceived)}</small>}
            </span>
          </div>
          <div className="cl-summary-item is-pending">
            <span className="cl-summary-label">Pending</span>
            <span className="cl-summary-value">{inr(totals.pending)}</span>
          </div>
          <div className="cl-summary-item">
            <span className="cl-summary-label">Collected</span>
            <span className="cl-summary-value">{totalCollPct}%</span>
          </div>
        </div>
      )}

      {/* client list */}
      {loader ? (
        <div className="cl-loader">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : viewClients.length === 0 ? (
        <div className="cl-empty">
          <SlIcon library="lucide" name="piggy-bank" />
          <div className="cl-empty-title">No clients match these filters</div>
          <div className="cl-empty-sub">Try a wider month range or clear the search.</div>
        </div>
      ) : (
        <div className="cl-list">
          {viewClients.map((b) => {
            const isOpen = !!expanded[b.id];
            const pendFg = b.pending > 0 ? "#c5221f" : "#188038";
            const pendBg = b.pending > 0 ? "#fdecea" : "#f2faf5";
            const pendBorder = b.pending > 0 ? "#f5cdc8" : "#d9efe1";
            return (
              <div className="cl-card" key={b.id}>
                {/* client header */}
                <div
                  className={`cl-card-head ${isOpen ? "is-open" : ""}`}
                  onClick={() => toggleClient(b.id)}
                >
                  <div className="cl-card-top">
                    <span className="cl-avatar">{b.initials}</span>
                    <div className="cl-card-info">
                      <div className="cl-card-name">{b.name}</div>
                      <div className="cl-card-sub">
                        <span
                          className="cl-chip"
                          style={{ background: b.cat.bg, color: b.cat.fg }}
                        >
                          {b.cat.label}
                        </span>
                        <span className="cl-ordercount">{b.orderCount} orders</span>
                      </div>
                    </div>
                    <SlIcon
                      library="lucide"
                      name="chevron-down"
                      className={`cl-chevron ${isOpen ? "is-open" : ""}`}
                    />
                  </div>

                  <div className="cl-stats">
                    <div className="cl-stat">
                      <div className="cl-stat-label">Order Value</div>
                      <div className="cl-stat-value">{inr(b.orderValue)}</div>
                    </div>
                    <div className="cl-stat">
                      <div className="cl-stat-label">Billed</div>
                      <div className="cl-stat-value">{inr(b.billed)}</div>
                      {b.gst > 0 && <div className="cl-stat-sub cl-gst">incl. {inr(b.gst)} GST</div>}
                      {b.burned > 0 && <div className="cl-stat-sub">{inr(b.burned)} excl. GST</div>}
                    </div>
                    <div className="cl-stat is-received">
                      <div className="cl-stat-label">Received</div>
                      <div className="cl-stat-value">{inr(b.received)}</div>
                      {b.tds > 0 && <div className="cl-stat-sub">{tdsNote(b.tds, b.tdsReceived)}</div>}
                    </div>
                    <div
                      className="cl-stat"
                      style={{ background: pendBg, borderColor: pendBorder }}
                    >
                      <div className="cl-stat-label" style={{ color: pendFg }}>Pending</div>
                      <div className="cl-stat-value" style={{ color: pendFg }}>{inr(b.pending)}</div>
                    </div>
                    <div className="cl-stat">
                      <div className="cl-collected-head">
                        <span className="cl-stat-label">Collected</span>
                        <span className="cl-collected-pct">{b.collPct}%</span>
                      </div>
                      <div className="cl-progress">
                        <div
                          className="cl-progress-bar"
                          style={{ width: `${b.collPct}%`, background: b.collColor }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* orders */}
                {isOpen && (
                  <div className="cl-orders">
                    {b.orders.map((o) => {
                      const open = panel[o.id];
                      const oPendFg = o.pending > 0 ? "#c5221f" : "#188038";
                      return (
                        <div className="cl-order" key={o.id}>
                          <div className="cl-order-body">
                            <div className="cl-order-top">
                              <div style={{ minWidth: 0 }}>
                                <div className="cl-order-name">{o.name}</div>
                                <div className="cl-order-meta">
                                  {o.id} · {monthLabelKey(o.month)}
                                </div>
                              </div>
                              <span
                                className="cl-status"
                                style={{ background: o.status.bg, color: o.status.fg }}
                              >
                                {o.status.label}
                              </span>
                            </div>

                            <div className="cl-order-stats">
                              <div>
                                <div className="cl-order-stat-label">Order Value</div>
                                <div className="cl-order-stat-value">{inr(o.orderValue)}</div>
                              </div>
                              <div>
                                <div className="cl-order-stat-label">Billed</div>
                                <div className="cl-order-stat-value">{inr(o.spent)}</div>
                                {o.gst > 0 && <div className="cl-order-stat-sub cl-gst">incl. {inr(o.gst)} GST</div>}
                                {o.burned > 0 && <div className="cl-order-stat-sub">{inr(o.burned)} excl. GST</div>}
                              </div>
                              <div>
                                <div className="cl-order-stat-label is-received">Received</div>
                                <div className="cl-order-stat-value is-received">{inr(o.received)}</div>
                                {o.tds > 0 && <div className="cl-order-stat-sub">{tdsNote(o.tds, o.tdsReceived)}</div>}
                              </div>
                              <div>
                                <div className="cl-order-stat-label" style={{ color: oPendFg }}>Pending</div>
                                <div className="cl-order-stat-value" style={{ color: oPendFg }}>{inr(o.pending)}</div>
                              </div>
                            </div>

                            <div className="cl-order-actions">
                              <button
                                className={`cl-toggle-btn ${open === "invoices" ? "is-active" : ""}`}
                                onClick={() => togglePanel(o.id, "invoices")}
                              >
                                <SlIcon library="lucide" name="receipt-text" />
                                Invoices
                                <span className="cl-badge">{o.invCount}</span>
                              </button>
                              <button
                                className={`cl-toggle-btn ${open === "transactions" ? "is-active" : ""}`}
                                onClick={() => togglePanel(o.id, "transactions")}
                              >
                                <SlIcon library="lucide" name="wallet" />
                                Transactions
                                <span className="cl-badge">{o.txnCount}</span>
                              </button>
                              <button
                                className="cl-toggle-btn cl-addpay-btn"
                                onClick={() => openPayModal(o)}
                              >
                                <SlIcon library="lucide" name="plus" />
                                Add Payment
                              </button>
                            </div>
                          </div>

                          {/* invoices panel */}
                          {open === "invoices" && (
                            <div className="cl-panel">
                              <div className="cl-panel-title">Invoices</div>
                              <div className="cl-panel-rows">
                                {(o.invs || []).length === 0 ? (
                                  <div className="cl-row-sub" style={{ padding: "6px 2px" }}>
                                    No invoices raised yet.
                                  </div>
                                ) : (
                                  o.invs.map((iv, i) => {
                                    const s = statusStyle(iv.status);
                                    return (
                                      <div className="cl-row" key={`${o.id}-inv-${i}`}>
                                        <SlIcon library="lucide" name="file-text" className="cl-row-lead" />
                                        <div className="cl-row-main">
                                          <div className="cl-row-primary mono">{iv.no}</div>
                                          <div className="cl-row-sub">{iv.date}</div>
                                        </div>
                                        <span
                                          className="cl-row-status"
                                          style={{ background: s.bg, color: s.fg }}
                                        >
                                          {iv.status}
                                        </span>
                                        <span className="cl-row-amount">{inr(iv.amount)}</span>
                                        {iv.url ? (
                                          <a
                                            className="cl-row-view"
                                            href={iv.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <span>View</span>
                                            <SlIcon library="lucide" name="external-link" />
                                          </a>
                                        ) : (
                                          <span className="cl-row-view is-disabled" title="No invoice document available">
                                            <span>View</span>
                                            <SlIcon library="lucide" name="external-link" />
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}

                          {/* transactions panel */}
                          {open === "transactions" && (
                            <div className="cl-panel">
                              <div className="cl-panel-title">Transactions</div>
                              <div className="cl-panel-rows">
                                {(o.txns || []).length === 0 ? (
                                  <div className="cl-row-sub" style={{ padding: "6px 2px" }}>
                                    No payments received yet.
                                  </div>
                                ) : (
                                  o.txns.map((t, i) => {
                                    const s = statusStyle(t.status);
                                    return (
                                      <div className="cl-row" key={`${o.id}-txn-${i}`}>
                                        <span className="cl-row-txn-ic">
                                          <SlIcon library="lucide" name={TXN_ICONS[t.mode] || "banknote"} />
                                        </span>
                                        <div className="cl-row-main">
                                          <div className="cl-row-primary">
                                            {t.mode}
                                            {t.tds > 0 ? (
                                              <span className="cl-row-tds">
                                                {" "}· TDS {inr(t.tds)}
                                                {t.tdsStatus ? ` (${t.tdsStatus.toLowerCase()})` : ""}
                                              </span>
                                            ) : null}
                                          </div>
                                          <div className="cl-txnid mono">
                                            <span className="cl-txnid-label">TXN</span>
                                            <span className="cl-txnid-val" title={t.txnId}>{t.txnId || "—"}</span>
                                            {t.txnId ? (
                                              <button
                                                type="button"
                                                className="cl-copy"
                                                title="Copy transaction ID"
                                                onClick={() => { try { navigator.clipboard.writeText(t.txnId); } catch (e) {} }}
                                              >
                                                <SlIcon library="lucide" name="copy" />
                                              </button>
                                            ) : null}
                                            <span className="cl-txnid-date"> · {t.date}</span>
                                          </div>
                                        </div>
                                        <span
                                          className="cl-row-status"
                                          style={{ background: s.bg, color: s.fg }}
                                        >
                                          {t.status}
                                        </span>
                                        <span className="cl-row-amount is-received">{inr(t.amount)}</span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* shared "Select Users" picker overlay (same design as other sales tabs) */}
      {showUsersFilter ? (
        <div className="usersMultiSelect" onClick={() => closeUsersFilter()}>
          <div class="popupuserlist" onClick={(e) => e.stopPropagation()}>
            <div class="userlist-header">
              <div class="alphabiticals">
                <ul>
                  {alphabets.map((alphabet) => (
                    <li
                      key={alphabet}
                      onClick={() => scrollToSection(alphabet)}
                      className={`${filterActiveLetters.includes(alphabet) ? "" : "inactive"}`}
                    >
                      {alphabet}
                    </li>
                  ))}
                </ul>
              </div>
              <ul class="new-user-type">
                <li key={0} onClick={() => handleNewUsrTypChange(0)}>
                  <label className={+newUserTypeInfilter === 0 ? "active" : ""} onClick={() => handleNewUsrTypChange(0)}>
                    All
                  </label>
                </li>
                {BrandsTypes.filter((item) => allowedNewUserTypes.includes(item.value)).map((item) => (
                  <li key={item.value} onClick={() => handleNewUsrTypChange(item.value)}>
                    <label
                      className={+newUserTypeInfilter === item.value ? "active" : ""}
                      onClick={() => handleNewUsrTypChange(item.value)}
                    >
                      {item.label}
                    </label>
                  </li>
                ))}
              </ul>
              <div class="searchbox">
                <input
                  class="form-control"
                  placeholder="search"
                  value={searchFilter}
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                    const tempString = e.target.value.toLowerCase();
                    let filteredArray = salesUsersList;
                    if (newUserTypeInfilter) filteredArray = filteredArray.filter((item) => item.new_user_type === newUserTypeInfilter);
                    if (tempString) {
                      filteredArray = filteredArray.filter((item) => item.label.toLowerCase().includes(tempString));
                    }
                    setTempSalesUsersList(filteredArray);
                    setFilterActiveLetters([...new Set(filteredArray.map((item) => item?.label[0]?.toUpperCase()).sort())]);
                  }}
                />
              </div>
            </div>

            <div class="userlistbody">
              {filterActiveLetters.map((alphabet) => {
                const filteredUsers = tempSalesUsersList.filter((item) => item.label[0].toUpperCase() === alphabet);
                return (
                  <div class="alphabiticalletterelement" key={alphabet}>
                    <div class="alphabiticalletter" id={alphabet}>
                      <span>{alphabet}</span>
                    </div>
                    <div class="userlist">
                      <ul>
                        {filteredUsers.map((user) => (
                          <li
                            key={user.value}
                            onClick={() => {
                              if (!selectedList.find((item) => item.value === user.value)) {
                                const next = [...selectedList, user];
                                setSelectedList(next);
                                localStorage.setItem("selectedSalesList", JSON.stringify(next.map((item) => item.value)));
                              } else {
                                const next = selectedList.filter((item) => item.value !== user.value);
                                setSelectedList(next);
                                localStorage.setItem("selectedSalesList", JSON.stringify(next.map((item) => item.value)));
                              }
                            }}
                            className={selectedList.find((item) => item.value === user.value) ? "active" : ""}
                          >
                            {user.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>

            <div class="userlistfooter">
              <div class="btnuserdetails">
                <button
                  class="selectall"
                  type="button"
                  onClick={() => {
                    setSelectedList([]);
                    localStorage.setItem("selectedSalesList", JSON.stringify([]));
                  }}
                >
                  Clear All
                </button>
                <button
                  class="selectonebyone"
                  type="button"
                  onClick={() => {
                    const next = [
                      ...new Set(
                        salesUsersList.filter((item) =>
                          [...selectedList.map((i) => i.value), ...tempSalesUsersList.map((i) => i.value)].includes(item.value)
                        )
                      ),
                    ];
                    setSelectedList(next);
                    localStorage.setItem("selectedSalesList", JSON.stringify(next.map((item) => item.value)));
                  }}
                >
                  Select All
                </button>
                <button
                  class={`selectonebyone ${selectedList.length === 0 ? "disabled" : ""}`}
                  type="button"
                  title={selectedList.length === 0 ? "Select Atleast a User" : ""}
                  onClick={() => closeUsersFilter()}
                >
                  Apply
                </button>
              </div>
            </div>
            <button className="close-btn" onClick={() => closeUsersFilter()}>
              <img src={closeSvg} alt="close2" />
            </button>
          </div>
        </div>
      ) : null}

      {/* record-payment modal (cheque / cash / IMPS — sales; needs finance approval) */}
      {payModal.open && (
        <div className="cl-modal-overlay" onClick={closePayModal}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cl-modal-head">
              <div>
                <div className="cl-modal-title">Record Payment</div>
                <div className="cl-modal-sub">{payModal.order?.name}</div>
              </div>
              <button className="cl-modal-close" onClick={closePayModal}>
                <SlIcon library="lucide" name="x" />
              </button>
            </div>

            <div className="cl-modal-body">
              <div className="cl-field2 cl-field2-wide cl-paytoggle">
                <button className={payForm.mode === "regular" ? "is-active" : ""} onClick={() => setPay("mode", "regular")}>
                  New payment
                </button>
                <button className={payForm.mode === "pool" ? "is-active" : ""} onClick={() => setPay("mode", "pool")}>
                  From multi-order payment
                </button>
              </div>

              {payForm.mode === "regular" ? (
                <>
                  <div className="cl-field2">
                    <label>Mode</label>
                    <select value={payForm.payment_type} onChange={(e) => setPay("payment_type", Number(e.target.value))}>
                      {PAY_MODES.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cl-field2">
                    <label>Amount <span className="req">*</span></label>
                    <input type="number" min="0" value={payForm.amount} onChange={(e) => setPay("amount", e.target.value)} placeholder="0" />
                  </div>
                  <div className="cl-field2 cl-field2-wide">
                    <label>Transaction ID <span className="req">*</span></label>
                    <input value={payForm.transaction_id} onChange={(e) => setPay("transaction_id", e.target.value)} placeholder="Cheque no / UTR / reference" />
                  </div>
                  <div className="cl-field2">
                    <label>PAN</label>
                    <input value={payForm.pan_no} onChange={(e) => setPay("pan_no", e.target.value)} placeholder="Optional" />
                  </div>
                </>
              ) : (
                <>
                  <div className="cl-field2 cl-field2-wide">
                    <label>Multi-order payment <span className="req">*</span></label>
                    <select value={payForm.mopd_id} onChange={(e) => setPay("mopd_id", e.target.value)}>
                      <option value="">{pools.length ? "— Select an approved payment —" : "No approved multi-order payments"}</option>
                      {pools.map((p) => (
                        <option key={p.mopd_id} value={p.mopd_id}>
                          {p.transaction_id} · {p.mode} · {inr(p.balance)} left
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="cl-field2">
                    <label>Amount <span className="req">*</span></label>
                    <input type="number" min="0" value={payForm.amount} onChange={(e) => setPay("amount", e.target.value)} placeholder="0" />
                  </div>
                </>
              )}

              <div className="cl-field2">
                <label>TDS</label>
                <input type="number" min="0" value={payForm.tds} onChange={(e) => setPay("tds", e.target.value)} placeholder="0" />
              </div>
              <div className="cl-field2">
                <label>Invoice</label>
                <select value={payForm.invoice_no} onChange={(e) => setPay("invoice_no", e.target.value)}>
                  <option value="">— None —</option>
                  {(payModal.order?.invs || []).map((iv) => (
                    <option key={iv.no} value={iv.no}>{iv.no}</option>
                  ))}
                </select>
              </div>
              <div className="cl-field2">
                <label>Payment Date</label>
                <input type="date" value={payForm.payment_date} onChange={(e) => setPay("payment_date", e.target.value)} />
              </div>
            </div>

            <div className="cl-modal-note">
              <SlIcon library="lucide" name="info" />
              {payForm.mode === "pool"
                ? "Allocated from an already-approved multi-order payment — counts as received right away."
                : "This payment goes to finance for approval before it counts as received."}
            </div>

            <div className="cl-modal-foot">
              <button className="cl-btn-ghost" onClick={closePayModal} disabled={paySubmitting}>Cancel</button>
              <button className="cl-btn-primary" onClick={submitPayment} disabled={paySubmitting}>
                {paySubmitting ? "Submitting…" : payForm.mode === "pool" ? "Allocate" : "Submit for approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* create a multi-order payment pool (one payment split across orders) */}
      {poolModal && (
        <div className="cl-modal-overlay" onClick={closePoolModal}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cl-modal-head">
              <div>
                <div className="cl-modal-title">New Multi-Order Payment</div>
                <div className="cl-modal-sub">One payment to be split across several orders</div>
              </div>
              <button className="cl-modal-close" onClick={closePoolModal}>
                <SlIcon library="lucide" name="x" />
              </button>
            </div>

            <div className="cl-modal-body">
              <div className="cl-field2">
                <label>Mode</label>
                <select value={poolForm.payment_type} onChange={(e) => setPoolField("payment_type", Number(e.target.value))}>
                  {PAY_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="cl-field2">
                <label>Total Amount <span className="req">*</span></label>
                <input type="number" min="0" value={poolForm.amount} onChange={(e) => setPoolField("amount", e.target.value)} placeholder="0" />
              </div>
              <div className="cl-field2 cl-field2-wide">
                <label>Transaction ID <span className="req">*</span></label>
                <input value={poolForm.transaction_id} onChange={(e) => setPoolField("transaction_id", e.target.value)} placeholder="Cheque no / UTR / reference" />
              </div>
              <div className="cl-field2 cl-field2-wide">
                <label>PAN</label>
                <input value={poolForm.pan_no} onChange={(e) => setPoolField("pan_no", e.target.value)} placeholder="Optional" />
              </div>
            </div>

            <div className="cl-modal-note">
              <SlIcon library="lucide" name="info" />
              Goes to finance for approval; once approved you can allocate it across orders.
            </div>

            <div className="cl-modal-foot">
              <button className="cl-btn-ghost" onClick={closePoolModal} disabled={poolSubmitting}>Cancel</button>
              <button className="cl-btn-primary" onClick={submitPool} disabled={poolSubmitting}>
                {poolSubmitting ? "Submitting…" : "Create — submit for approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Collections;
